import React, { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '@naf-simba/elements-buttons';
import { ConsumerTestProductListType } from '@appTypes/ConsumerTestProductType';
import { useTrackPromotion } from '@hooks/useTrackPromotion';
import { getPromotionEventTrackingData } from '@utils/Tracking/Promotion/getPromotionEventTrackingData';
import { mapSimbaVariant } from '@utils/simba';
import { useTrackCTA } from '@utils/Tracking/CtaClick/TrackCtaClick';
import { ConsumerTestProduct } from './ConsumerTestProduct';
import { ConsumerTestCtaWrapper, ProductListContainer, Title } from './ConsumerTestProductList.styles';

interface Props {
  value: ConsumerTestProductListType;
}

export const ConsumerTestProductList = ({ value }: Props) => {
  const { title, products, numProducts, sortBy, internalLink } = value;
  const navigate = useNavigate();
  const ctaLink = `/${internalLink?.parent?.slug ?? 'forbrukertester'}/${internalLink?.slug}`;
  const promotion = getPromotionEventTrackingData(internalLink?.name, ctaLink);
  const trackCTA = useTrackCTA();
  const buttonVariant = mapSimbaVariant('secondary');
  const shouldTrackPromotion = Boolean(promotion);
  const { wrapperRef, trackSelectPromotion } = useTrackPromotion({
    creative_slot: 'consumer-test-product-list',
    creative_name: `CTA | secondary | ${internalLink?.name}`,
    promotion_id: promotion?.promotion_id,
    promotion_name: promotion?.promotion_name,
  });

  const sortedAndTrimmedProducts = useMemo(
    () =>
      [...products]
        .sort((a, b) => {
          switch (sortBy) {
            case 'sortByPoints':
              return b.scoreValue - a.scoreValue;
            case 'sortByPrice':
              return a.priceValue - b.priceValue;
            case 'sortByName':
              return a.title.localeCompare(b.title);
            default:
              return 0;
          }
        })
        .slice(0, numProducts ?? products.length),
    [products, sortBy, numProducts],
  );

  const handleCTAClick = () => {
    if (shouldTrackPromotion) {
      trackSelectPromotion();
    }
    trackCTA({ cta_position: 'content', text: internalLink.name, url: ctaLink });
    navigate(ctaLink);
  };

  return (
    <ProductListContainer>
      <Title>{title}</Title>
      {sortedAndTrimmedProducts.map((product) => (
        <ConsumerTestProduct key={product.id} product={product} category={internalLink.slug} />
      ))}
      <ConsumerTestCtaWrapper>
        <div ref={shouldTrackPromotion ? wrapperRef : undefined}>
          <Button
            variant={buttonVariant.variant}
            color={buttonVariant.color}
            shade={buttonVariant.shade}
            onClick={handleCTAClick}
          >
            {internalLink.name}
          </Button>
        </div>
      </ConsumerTestCtaWrapper>
    </ProductListContainer>
  );
};
